import React from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { BookmarkCheck, Play, X, ShoppingBag, ShoppingCart, ChevronDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { getCategoryLabel, getCategoryEmoji } from '@/lib/tbrCategorization';
import type { TBRCategory } from '@/lib/tbrCategorization';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TBRRowContentProps {
  noteText?: string;
  addedAt?: string;
  priority?: 'high' | 'medium' | 'low';
  isPurchased?: boolean;
  tbrCategory?: TBRCategory;
  userBookId?: string;
  onMarkAsReading?: () => void;
  onRemove?: () => void;
  onTogglePurchased?: (purchased: boolean) => void;
  onCategoryChange?: (category: TBRCategory) => void;
  loading?: boolean;
}

function relativeTime(dateString: string): string {
  try {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  } catch {
    return 'some time ago';
  }
}

export function TBRRowContent({
  noteText,
  addedAt,
  priority,
  isPurchased,
  tbrCategory,
  userBookId,
  onMarkAsReading,
  onRemove,
  onTogglePurchased,
  onCategoryChange,
  loading = false
}: TBRRowContentProps) {
  const [categoryLabel, setCategoryLabel] = React.useState<string>('');
  const [categoryEmoji, setCategoryEmoji] = React.useState<string>('');
  const [updating, setUpdating] = React.useState(false);
  const [allCategories, setAllCategories] = React.useState<Array<{ value: TBRCategory; label: string; emoji: string }>>([]);

  React.useEffect(() => {
    const loadCategoryData = async () => {
      const categories: TBRCategory[] = ['quick_read', 'challenge_read', 'mood_read', 'palate_cleanser', 'uncategorized'];
      const categoryData = await Promise.all(
        categories.map(async (cat) => ({
          value: cat,
          label: await getCategoryLabel(cat),
          emoji: await getCategoryEmoji(cat),
        }))
      );
      setAllCategories(categoryData);
    };
    
    loadCategoryData();
  }, []);

  React.useEffect(() => {
    if (tbrCategory) {
      getCategoryLabel(tbrCategory).then(setCategoryLabel);
      getCategoryEmoji(tbrCategory).then(setCategoryEmoji);
    }
  }, [tbrCategory]);

  const handleCategoryChange = async (newCategory: TBRCategory) => {
    if (!userBookId || updating) return;
    
    setUpdating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('You must be logged in to update categories');
        return;
      }

      const { error } = await supabase
        .from('user_books')
        .update({ tbr_category: newCategory })
        .eq('id', userBookId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast.success('Category updated successfully');
      onCategoryChange?.(newCategory);
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      {/* Status and metadata row */}
      <div className="flex items-center gap-2 flex-wrap">
        <Badge variant="secondary" className="text-xs">
          Want to read
        </Badge>
        
        {userBookId && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="focus:outline-none"
                disabled={updating}
              >
                <Badge 
                  variant="outline" 
                  className="text-xs cursor-pointer hover:bg-accent transition-colors flex items-center gap-1"
                >
                  {tbrCategory && tbrCategory !== 'uncategorized' ? (
                    <>
                      {categoryEmoji} {categoryLabel}
                    </>
                  ) : (
                    <>📚 Uncategorized</>
                  )}
                  <ChevronDown className="w-3 h-3" />
                </Badge>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              className="z-50 bg-popover border shadow-lg"
              align="start"
            >
              {allCategories.map((cat) => (
                <DropdownMenuItem
                  key={cat.value}
                  onClick={() => handleCategoryChange(cat.value)}
                  className="cursor-pointer flex items-center gap-2"
                  disabled={updating || cat.value === tbrCategory}
                >
                  <span>{cat.emoji}</span>
                  <span>{cat.label}</span>
                  {cat.value === tbrCategory && (
                    <span className="ml-auto text-xs text-muted-foreground">✓</span>
                  )}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        
        {isPurchased ? (
          <Badge variant="default" className="text-xs bg-green-600 hover:bg-green-700">
            <ShoppingBag className="w-3 h-3 mr-1" />
            Owned
          </Badge>
        ) : (
          <Badge variant="outline" className="text-xs">
            <ShoppingCart className="w-3 h-3 mr-1" />
            Not Purchased
          </Badge>
        )}
        
        {priority && (
          <Badge 
            variant={priority === 'high' ? 'destructive' : priority === 'medium' ? 'default' : 'outline'} 
            className="text-xs"
          >
            {priority} priority
          </Badge>
        )}
        
        {addedAt && (
          <span className="ml-auto text-xs text-muted-foreground flex-shrink-0">
            Added {relativeTime(addedAt)}
          </span>
        )}
      </div>

      {/* Notes */}
      {noteText ? (
        <div className="text-sm sm:text-base leading-relaxed whitespace-pre-line break-words">
          {noteText}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          No notes added.
        </p>
      )}

      {/* Action buttons */}
      <TooltipProvider>
        <div className="flex flex-wrap gap-2 mt-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="default"
                onClick={onMarkAsReading}
                disabled={loading}
                className="flex-1 min-w-[40px] sm:min-w-[140px]"
                aria-label="Start Reading"
              >
                <Play className="w-4 h-4 sm:w-3 sm:h-3 sm:mr-1 flex-shrink-0" />
                <span className="hidden sm:inline whitespace-nowrap">Start Reading</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="sm:hidden">
              <p>Start Reading</p>
            </TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant={isPurchased ? "outline" : "secondary"}
                onClick={() => onTogglePurchased?.(!isPurchased)}
                disabled={loading}
                className="flex-1 min-w-[40px] sm:min-w-[180px]"
                aria-label={isPurchased ? "Mark as Not Purchased" : "Mark as Purchased"}
              >
                {isPurchased ? (
                  <>
                    <ShoppingBag className="w-4 h-4 sm:w-3 sm:h-3 sm:mr-1 flex-shrink-0" />
                    <span className="hidden sm:inline whitespace-nowrap">Mark as Not Purchased</span>
                  </>
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 sm:w-3 sm:h-3 sm:mr-1 flex-shrink-0" />
                    <span className="hidden sm:inline whitespace-nowrap">Mark as Purchased</span>
                  </>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent className="sm:hidden">
              <p>{isPurchased ? "Mark as Not Purchased" : "Mark as Purchased"}</p>
            </TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                onClick={onRemove}
                disabled={loading}
                className="flex-1 min-w-[40px] sm:min-w-[110px]"
                aria-label="Remove"
              >
                <X className="w-4 h-4 sm:w-3 sm:h-3 sm:mr-1 flex-shrink-0" />
                <span className="hidden sm:inline whitespace-nowrap">Remove</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent className="sm:hidden">
              <p>Remove</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    </>
  );
}

export default TBRRowContent;